import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, AlertTriangle, Loader2, ArrowRight, CreditCard } from 'lucide-react';
import { Logo } from '../components/Logo';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Stripe success-redirect landing page for penalty payments.
 * Verifies the session, flips penalty to paid, then shows the result.
 *
 * When the worker is signed in AND has more outstanding penalties,
 * we surface a "You still have £X — Settle next" follow-on CTA that
 * starts the next Stripe Checkout in one click. This creates a
 * Stripe-style payment-chain UX without forcing them anywhere.
 *
 * URL: /penalty-pay/result?session_id=...&penalty_id=...
 */
export default function PenaltyPaymentResultPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const penaltyId = params.get('penalty_id');
  const [status, setStatus] = useState('verifying');
  const [amount, setAmount] = useState(null);
  const [error, setError] = useState(null);

  // Follow-on outstanding-balance state (only fetched when signed in)
  const { api, user } = useAuth();
  const [followOn, setFollowOn] = useState(null);
  const [settlingNext, setSettlingNext] = useState(false);

  // 1) Verify the Stripe session
  useEffect(() => {
    if (!sessionId || !penaltyId) {
      setStatus('error');
      setError('Missing payment reference.');
      return;
    }
    (async () => {
      try {
        const res = await axios.post(
          `${API}/penalties/pay/verify/${penaltyId}?session_id=${encodeURIComponent(sessionId)}`
        );
        if (res.data?.status === 'paid') {
          setStatus('paid');
          setAmount(res.data?.amount);
        } else {
          setStatus('pending');
        }
      } catch (e) {
        setStatus('error');
        setError(e.response?.data?.detail || 'Verification failed');
      }
    })();
  }, [sessionId, penaltyId]);

  // 2) When paid + signed in as worker, fetch remaining outstanding balance
  useEffect(() => {
    if (status !== 'paid') return;
    if (!user || user.role !== 'worker') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/worker/outstanding-penalties');
        if (!cancelled) setFollowOn(res.data);
      } catch {
        // silent — no follow-on shown
      }
    })();
    return () => { cancelled = true; };
  }, [status, user, api]);

  const handleSettleNext = async () => {
    if (!followOn?.oldest?.id) return;
    setSettlingNext(true);
    try {
      const res = await api.post(`/penalties/${followOn.oldest.id}/charge`, {
        originUrl: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        setSettlingNext(false);
      }
    } catch {
      setSettlingNext(false);
    }
  };

  const hasMore = followOn && followOn.count > 0;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="penalty-result-page">
      <header className="bg-[#0B132B] text-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" variant="light" linkTo="/" />
          <Link to="/" className="text-[12.5px] text-white/70 hover:text-white">Home</Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6 py-14">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
            {status === 'verifying' && (
              <>
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 border border-slate-200 mx-auto mb-4">
                  <Loader2 className="h-6 w-6 text-slate-500 animate-spin" />
                </span>
                <p className="text-[16px] font-bold text-slate-900">Verifying payment…</p>
                <p className="text-[13px] text-slate-500 mt-1">Checking your Stripe session.</p>
              </>
            )}
            {status === 'paid' && (
              <>
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 border border-emerald-200 mx-auto mb-4">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                </span>
                <p className="text-[18px] font-bold text-slate-900">Payment successful</p>
                <p className="text-[13.5px] text-slate-500 mt-1">
                  Your £{Number(amount || 0).toFixed(2)} penalty has been settled. Thank you.
                </p>
              </>
            )}
            {status === 'pending' && (
              <>
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 border border-amber-200 mx-auto mb-4">
                  <AlertTriangle className="h-6 w-6 text-amber-600" />
                </span>
                <p className="text-[16px] font-bold text-slate-900">Payment pending</p>
                <p className="text-[13.5px] text-slate-500 mt-1">
                  Stripe is still processing your payment. We'll update your account once it completes.
                </p>
              </>
            )}
            {status === 'error' && (
              <>
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 border border-red-200 mx-auto mb-4">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </span>
                <p className="text-[16px] font-bold text-slate-900">Could not verify</p>
                <p className="text-[13.5px] text-slate-500 mt-1">{error || 'Please contact support.'}</p>
              </>
            )}

            <Link
              to={user?.role === 'worker' ? '/worker' : '/'}
              className="mt-6 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#DA291C] hover:text-[#b91f13]"
            >
              {user?.role === 'worker' ? 'Back to dashboard' : 'Return home'} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {/* Follow-on CTA — only when paid + still outstanding */}
          {status === 'paid' && hasMore && (
            <div
              className="mt-5 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 p-5 sm:p-6"
              data-testid="penalty-followup-cta"
            >
              <div className="flex items-start gap-3 mb-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 border border-amber-200 shrink-0">
                  <AlertTriangle className="h-4 w-4 text-amber-700" strokeWidth={2} />
                </span>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700">Still outstanding</p>
                  <h3 className="text-lg font-bold text-slate-900 tracking-tight tabular-nums mt-0.5">
                    £{Number(followOn.totalOwed || 0).toFixed(2)}
                  </h3>
                  <p className="text-[13px] text-amber-900/80 mt-1 leading-relaxed">
                    You have {followOn.count} more penalt{followOn.count === 1 ? 'y' : 'ies'} on your account.
                    Settle the next one in one click.
                  </p>
                </div>
              </div>
              <button
                onClick={handleSettleNext}
                disabled={settlingNext}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white px-5 h-11 text-[13px] font-semibold transition-colors shadow-[0_8px_24px_-6px_rgba(218,41,28,0.4)] disabled:opacity-60"
                data-testid="penalty-followup-settle-btn"
              >
                {settlingNext ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                Settle £{Number(followOn.oldest?.amount || 0).toFixed(2)} next
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
